import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { Brand } from 'app/components/Icons'
import { Media } from 'app/theme/media'
import React, { memo } from 'react'
import { useInView } from 'react-intersection-observer'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

import { Carousel } from './Carousel'

export interface Props {
  description?: string
  images?: ImageProps[]
  subtitle?: string
  title?: string
}

export const AdvancedIntro = memo(function AdvancedIntro({
  description,
  images,
  subtitle,
  title,
}: Props) {
  const [animObjectRef, isAnimObjectVisible] = useInView({
    threshold: 0.25,
    triggerOnce: true,
  })

  return (
    <Container>
      <Logo>
        <FadeInUp>
          <Brand />
        </FadeInUp>
      </Logo>

      <Head
        className={isAnimObjectVisible ? 'visible' : undefined}
        ref={animObjectRef}
      >
        <Lines />

        <Wrapper>
          {title ? <Title text={title} /> : null}

          {subtitle ? (
            <FadeInUp delay={0.4}>
              <Subtitle>{subtitle}</Subtitle>
            </FadeInUp>
          ) : null}

          {description ? (
            <FadeInUp delay={0.6}>
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            </FadeInUp>
          ) : null}

          {images && images[1] ? (
            <Media lessThan="desktopSmall">
              <LazyLoadComponent>
                <Thumb>
                  <Image {...images[1]} />
                </Thumb>
              </LazyLoadComponent>
            </Media>
          ) : null}
        </Wrapper>
      </Head>

      {images ? (
        <Media greaterThanOrEqual="desktopSmall">
          <Carousel images={images} />
        </Media>
      ) : null}

      <Media greaterThanOrEqual="desktopSmall">
        <Background />
      </Media>
    </Container>
  )
})

const Container = styled.section`
  overflow: hidden;
  margin-top: 9rem;
  padding-bottom: 9.75rem;
  position: relative;
  text-align: center;

  @media (max-width: 1199px) {
    margin-top: 5.625rem;
    padding-bottom: 0;
  }
`

const Logo = styled.div`
  display: inline-block;
  background: ${({ theme }) => theme.colors.variants.neutralLight5};
  margin-bottom: 3.75rem;
  padding: 0 2.25rem;
  position: relative;
  z-index: 2;

  svg {
    width: auto;
    height: 3.25rem;
    fill: ${({ theme }) => theme.colors.variants.primaryDark};
  }

  @media (max-width: 1199px) {
    background: none;
    margin-bottom: 1.875rem;
    padding: 0;

    svg {
      height: 2.5rem;
    }
  }
`

const Lines = styled.div`
  &:before,
  &:after {
    content: '';
    width: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    top: -5.375rem;
    bottom: calc(100% + 5.375rem);
    transition: 1.4s ease-in-out;
  }
  &:before {
    left: 10vw;
  }
  &:after {
    right: 10vw;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`

const Head = styled.div`
  position: relative;
  &:before {
    content: '';
    height: 0.0625rem;
    background: ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    top: -5.375rem;
    right: 50%;
    left: 50%;
    transition: 1.4s ease-in-out;
  }
  &.visible {
    &:before {
      right: 10vw;
      left: 10vw;
    }

    ${Lines} {
      &:before,
      &:after {
        bottom: 0;
        transition-delay: 1.4s;
      }
    }
  }

  @media (max-width: 1199px) {
    &:before {
      display: none;
    }
  }
`

const Wrapper = styled.div`
  display: inline-block;
  max-width: 30rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight5};
  margin: auto;
  padding: 0 5.25rem 5.25rem;
  position: relative;
  z-index: 3;

  @media (max-width: 1199px) {
    max-width: none;
    padding: 0 1.875rem;
  }
`

const Title = styled(AnimatedTitle)`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.5rem;
  font-weight: 500;
  line-height: 2.5rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    font-size: 1.875rem;
    line-height: 1.875rem;
  }
`

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.125rem;
  font-weight: 300;
  line-height: 1.125rem;
  margin-top: 0.5625rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    font-size: 1rem;
    line-height: 1rem;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.primaryDark};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  font-weight: 300;
  line-height: 1.375rem;
  margin-top: 1.875rem;

  p {
    margin-block-end: 1.5em;
  }
`

const Thumb = styled.div`
  aspect-ratio: 1.5;
  margin-top: 3.75rem;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }
`

const Background = styled.div`
  height: 26.375rem;
  background: url('/svg/pattern-light.svg') top center;
  position: absolute;
  bottom: 0;
  right: 5vw;
  left: 5vw;
`
